import * as THREE from "three";
import { SCENE_OBSTACLES } from "./obstacles";

// Grid-based A* pathfinding on the XZ plane
const GRID_CELL = 0.5; // cell size in world units
const GRID_MIN = -55; // world min x/z
const GRID_MAX = 55; // world max x/z
const GRID_SIZE = Math.ceil((GRID_MAX - GRID_MIN) / GRID_CELL); // cells per axis
const AVATAR_PADDING = 0.4; // extra clearance around obstacles

// Pre-computed blocked grid
let blockedGrid: Uint8Array | null = null;

function worldToGrid(wx: number, wz: number): [number, number] {
  const gx = Math.floor((wx - GRID_MIN) / GRID_CELL);
  const gz = Math.floor((wz - GRID_MIN) / GRID_CELL);
  return [
    Math.max(0, Math.min(GRID_SIZE - 1, gx)),
    Math.max(0, Math.min(GRID_SIZE - 1, gz)),
  ];
}

function gridToWorld(gx: number, gz: number): [number, number] {
  return [
    GRID_MIN + (gx + 0.5) * GRID_CELL,
    GRID_MIN + (gz + 0.5) * GRID_CELL,
  ];
}

function gridIndex(gx: number, gz: number): number {
  return gz * GRID_SIZE + gx;
}

function isBlocked(gx: number, gz: number): boolean {
  if (gx < 0 || gx >= GRID_SIZE || gz < 0 || gz >= GRID_SIZE) return true;
  return blockedGrid![gridIndex(gx, gz)] === 1;
}

function buildGrid() {
  blockedGrid = new Uint8Array(GRID_SIZE * GRID_SIZE);

  for (let gx = 0; gx < GRID_SIZE; gx++) {
    for (let gz = 0; gz < GRID_SIZE; gz++) {
      const [wx, wz] = gridToWorld(gx, gz);

      for (const obs of SCENE_OBSTACLES) {
        const dx = wx - obs.center.x;
        const dz = wz - obs.center.z;
        let blocked = false;
        if (obs.type === "circle") {
          const dist = Math.sqrt(dx * dx + dz * dz);
          blocked = dist < obs.radius + AVATAR_PADDING;
        } else {
          // Transform the test point into the rect's local (rotated) frame
          // and then do a simple AABB check.
          const rot = obs.rotation ?? 0;
          let lx = dx;
          let lz = dz;
          if (rot !== 0) {
            const cos = Math.cos(rot);
            const sin = Math.sin(rot);
            lx = dx * cos - dz * sin;
            lz = dx * sin + dz * cos;
          }
          blocked =
            Math.abs(lx) < obs.halfWidth + AVATAR_PADDING &&
            Math.abs(lz) < obs.halfDepth + AVATAR_PADDING;
        }
        if (blocked) {
          blockedGrid[gridIndex(gx, gz)] = 1;
          break;
        }
      }
    }
  }
}

// A* with 8-directional movement
interface AStarNode {
  gx: number;
  gz: number;
  g: number;
  f: number;
  parent: AStarNode | null;
}

function heuristic(ax: number, az: number, bx: number, bz: number): number {
  const dx = Math.abs(ax - bx);
  const dz = Math.abs(az - bz);
  // Octile distance
  return Math.max(dx, dz) + (Math.SQRT2 - 1) * Math.min(dx, dz);
}

const DIRS = [
  [1, 0], [-1, 0], [0, 1], [0, -1],
  [1, 1], [1, -1], [-1, 1], [-1, -1],
];

export function findPath(
  start: THREE.Vector3,
  end: THREE.Vector3
): THREE.Vector3[] {
  if (!blockedGrid) buildGrid();

  const [sx, sz] = worldToGrid(start.x, start.z);
  const [ex, ez] = worldToGrid(end.x, end.z);

  // If target is blocked, find nearest unblocked cell
  let targetX = ex;
  let targetZ = ez;
  if (isBlocked(ex, ez)) {
    let bestDist = Infinity;
    const searchRadius = 10;
    for (let dx = -searchRadius; dx <= searchRadius; dx++) {
      for (let dz = -searchRadius; dz <= searchRadius; dz++) {
        const nx = ex + dx;
        const nz = ez + dz;
        if (!isBlocked(nx, nz)) {
          const d = dx * dx + dz * dz;
          if (d < bestDist) {
            bestDist = d;
            targetX = nx;
            targetZ = nz;
          }
        }
      }
    }
  }

  // If start is blocked, just return direct path (shouldn't happen normally)
  if (isBlocked(sx, sz)) {
    return [end.clone()];
  }

  // A* search
  const openMap = new Map<number, AStarNode>();
  const closedSet = new Set<number>();

  const startNode: AStarNode = {
    gx: sx,
    gz: sz,
    g: 0,
    f: heuristic(sx, sz, targetX, targetZ),
    parent: null,
  };
  openMap.set(gridIndex(sx, sz), startNode);

  let iterations = 0;
  const MAX_ITERATIONS = 5000;

  while (openMap.size > 0 && iterations < MAX_ITERATIONS) {
    iterations++;

    // Find node with lowest f in open set
    let bestNode: AStarNode | null = null;
    let bestF = Infinity;
    for (const node of openMap.values()) {
      if (node.f < bestF) {
        bestF = node.f;
        bestNode = node;
      }
    }

    if (!bestNode) break;

    // Reached target?
    if (bestNode.gx === targetX && bestNode.gz === targetZ) {
      return reconstructPath(bestNode, end);
    }

    const key = gridIndex(bestNode.gx, bestNode.gz);
    openMap.delete(key);
    closedSet.add(key);

    // Expand neighbors
    for (const [dx, dz] of DIRS) {
      const nx = bestNode.gx + dx;
      const nz = bestNode.gz + dz;
      const nKey = gridIndex(nx, nz);

      if (isBlocked(nx, nz) || closedSet.has(nKey)) continue;

      // For diagonal movement, check that both adjacent cells are clear
      if (dx !== 0 && dz !== 0) {
        if (isBlocked(bestNode.gx + dx, bestNode.gz) ||
            isBlocked(bestNode.gx, bestNode.gz + dz)) {
          continue;
        }
      }

      const moveCost = dx !== 0 && dz !== 0 ? Math.SQRT2 : 1;
      const newG = bestNode.g + moveCost;

      const existing = openMap.get(nKey);
      if (existing && newG >= existing.g) continue;

      const newNode: AStarNode = {
        gx: nx,
        gz: nz,
        g: newG,
        f: newG + heuristic(nx, nz, targetX, targetZ),
        parent: bestNode,
      };
      openMap.set(nKey, newNode);
    }
  }

  // No path found — just go direct
  return [end.clone()];
}

function reconstructPath(
  node: AStarNode,
  finalTarget: THREE.Vector3
): THREE.Vector3[] {
  const gridPath: [number, number][] = [];
  let current: AStarNode | null = node;
  while (current) {
    gridPath.unshift([current.gx, current.gz]);
    current = current.parent;
  }

  // Simplify path: remove collinear waypoints
  const simplified: [number, number][] = [gridPath[0]];
  for (let i = 1; i < gridPath.length - 1; i++) {
    const prev = gridPath[i - 1];
    const curr = gridPath[i];
    const next = gridPath[i + 1];
    const dx1 = curr[0] - prev[0];
    const dz1 = curr[1] - prev[1];
    const dx2 = next[0] - curr[0];
    const dz2 = next[1] - curr[1];
    // Keep waypoint if direction changes
    if (dx1 !== dx2 || dz1 !== dz2) {
      simplified.push(curr);
    }
  }
  simplified.push(gridPath[gridPath.length - 1]);

  // Convert to world positions
  const waypoints = simplified.map(([gx, gz]) => {
    const [wx, wz] = gridToWorld(gx, gz);
    return new THREE.Vector3(wx, 0, wz);
  });

  // Replace last waypoint with exact target
  if (waypoints.length > 0) {
    waypoints[waypoints.length - 1].copy(finalTarget);
    waypoints[waypoints.length - 1].y = 0;
  }

  // Skip first waypoint (current position)
  if (waypoints.length > 1) {
    waypoints.shift();
  }

  return waypoints;
}
